<template>
  <div class="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
    <div class="grid grid-cols-12 gap-4 items-center">
      <!-- Name and Email Column (takes up 4 columns) -->
      <div class="col-span-4 min-w-0 flex items-center gap-2">
        <div
          v-if="referrer.user_details && referrer.user_details.avatar_image"
          class="w-10 h-10 rounded-full overflow-hidden ring-1 ring-gray-200"
        >
          <img
            :src="referrer.user_details.avatar_image"
            alt="Profile Picture"
            class="w-10 h-10 object-cover"
            :class="{ 'privacy-blur': isBlurred }"
          />
        </div>
        <div
          v-else
          class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm"
        >
          <span :class="{ 'privacy-blur': isBlurred }">{{ referrerInitials }}</span>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h4 class="text-sm font-medium truncate" :class="{ 'privacy-blur': isBlurred }">
              {{ referrer.name }}
            </h4>
            <a
              v-if="!isBlurred && referrer.linkedin_url"
              :href="referrer.linkedin_url"
              target="_blank"
              class="text-sm text-gray-500 flex-shrink-0"
            >
              <img src="/linkedin_logo.svg" alt="LinkedIn" class="w-4 h-4" />
            </a>
          </div>
          <p class="text-sm text-gray-500 truncate" :class="{ 'privacy-blur': isBlurred }">
            {{ referrer.email || 'No email provided' }}
          </p>
          <!-- Latest referral compact badge (only when a confirmed date is available) -->
          <HoverCard v-if="referrer.latest_referral?.referral_confirmed_at" :open-delay="0" :close-delay="0">
            <HoverCardTrigger as-child>
              <span class="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 mt-1 rounded-full bg-cosmicPurple/10 text-cosmicPurple border border-cosmicPurple/20 max-w-full truncate cursor-default">
                <BadgeCheck class="w-3 h-3 flex-shrink-0" />
                <span class="truncate">{{ latestReferralLabel(referrer.latest_referral) }}</span>
              </span>
            </HoverCardTrigger>
            <HoverCardContent class="w-auto p-3 text-sm space-y-0.5">
              <p class="font-medium text-gray-800">
                {{ referrer.latest_referral.applicant.first_name }} {{ referrer.latest_referral.applicant.last_name }}
              </p>
              <p class="text-xs text-gray-500">
                {{ referrer.latest_referral.role_name }} · {{ referrer.latest_referral.employer_name }}
              </p>
              <p class="text-xs text-gray-400">{{ formatDate(referrer.latest_referral.referral_confirmed_at) }}</p>
            </HoverCardContent>
          </HoverCard>
        </div>
      </div>

      <!-- Default Toggle Column (1 column) -->
      <div class="col-span-1 flex justify-center">
        <div
          class="cursor-pointer"
          :title="isDefault ? 'Default referrer' : 'Set as default'"
          @click="setAsDefault"
        >
          <CheckCircle v-if="isDefault" class="w-5 h-5 text-green-600 hover:text-green-700" />
          <Circle v-else class="w-5 h-5 text-gray-200 hover:text-gray-500" />
        </div>
      </div>

      <!-- Status Dropdown Column (2 columns) -->
      <div class="col-span-2">
        <Select
          :model-value="referrer.is_active ? 'active' : 'inactive'"
          @update:model-value="handleStatusChange"
        >
          <SelectTrigger
            class="h-7 py-0 text-xs w-full"
            :class="{
              'bg-green-100 text-green-800 border-green-200': referrer.is_active,
              'bg-red-100 text-red-800 border-red-200': !referrer.is_active
            }"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <!-- Reach Dropdown Column (2 columns) -->
      <div class="col-span-2">
        <Select
          :model-value="referrer.reachability || undefined"
          @update:model-value="handleReachDifficultyChange"
        >
          <SelectTrigger
            class="h-7 py-0 text-xs w-full bg-white"
            :class="{
              'bg-yellow-100 text-yellow-800 border-yellow-200': referrer.reachability === 'EASY',
              'bg-red-100 text-red-800 border-red-200': referrer.reachability === 'HARD',
              'bg-white text-gray-800 border-gray-200': !referrer.reachability
            }"
          >
            <SelectValue
              :placeholder="
                referrer.reachability
                  ? formatReachDifficulty(referrer.reachability)
                  : 'Reachability'
              "
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            <SelectItem value="EASY">Easy</SelectItem>
            <SelectItem value="HARD">Hard</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <!-- Relationship Dropdown Column (2 columns) -->
      <div class="col-span-2">
        <Select
          :model-value="referrer.relationship_type || undefined"
          @update:model-value="handleRelationshipChange"
        >
          <SelectTrigger class="h-7 py-0 text-xs w-full bg-white">
            <SelectValue
              :placeholder="
                referrer.relationship_type
                  ? formatRelationshipType(referrer.relationship_type)
                  : 'Relationship'
              "
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NON_GRAD_CHAMPION">Non-Grad Champion</SelectItem>
            <SelectItem value="GRAD">Grad</SelectItem>
            <SelectItem value="RECRUITER_MANAGER">Recruiter Manager</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <!-- Expand/Collapse Button Column (1 column) -->
      <div class="col-span-1 flex justify-center">
        <Button variant="outline" size="sm" @click="toggleExpansion">
          <ChevronDown
            class="w-4 h-4 transition-transform duration-200"
            :class="{ 'transform rotate-180': isExpanded }"
          />
        </Button>
      </div>
    </div>

    <!-- Expanded Content -->
    <div v-if="isExpanded" class="mt-3 pt-3 border-t border-gray-200">
      <div class="grid grid-cols-2 gap-6 text-sm">
        <!-- Left Side: Basic Info and Follow Up Method -->
        <div class="space-y-4 text-sm">
          <!-- Name -->
          <div>
            <label class="font-medium text-gray-700 mb-1 block">Name</label>
            <input
              v-model="localReferrer.name"
              :class="[
                'w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500',
                { 'privacy-blur': isBlurred }
              ]"
              @input="handleInputChange('name', $event.target.value)"
            />
          </div>

          <!-- Email -->
          <div>
            <label class="font-medium text-gray-700 mb-1 block">Email</label>
            <input
              v-model="localReferrer.email"
              type="email"
              :class="[
                'w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm',
                { 'privacy-blur': isBlurred }
              ]"
              @input="handleInputChange('email', $event.target.value)"
            />
          </div>

          <!-- LinkedIn URL -->
          <div>
            <label class="font-medium text-gray-700 mb-1 block">LinkedIn URL</label>
            <input
              v-model="localReferrer.linkedin_url"
              type="url"
              placeholder="https://linkedin.com/in/username"
              :class="[
                'w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm',
                { 'privacy-blur': isBlurred }
              ]"
              @input="handleInputChange('linkedin_url', $event.target.value)"
            />
          </div>

          <!-- Follow Up Method -->
          <div>
            <label class="font-medium text-gray-700 mb-1 block">Follow Up Method</label>
            <input
              v-model="localReferrer.follow_up_method"
              placeholder="e.g., Email, Phone, LinkedIn"
              class="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              @input="handleInputChange('follow_up_method', $event.target.value)"
            />
          </div>

          <!-- Passion Area -->
          <div>
            <label class="font-medium text-gray-700 mb-1 block">Passion Area</label>
            <input
              v-model="localReferrer.passion_area"
              placeholder="e.g., Technology, Healthcare, Finance"
              class="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              @input="handleInputChange('passion_area', $event.target.value)"
            />
          </div>
        </div>

        <!-- Right Side: Notes -->
        <div>
          <label class="font-medium text-gray-700 mb-1 block">Notes</label>
          <textarea
            v-model="localReferrer.notes"
            placeholder="Add any additional notes about this referrer..."
            class="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[200px] resize-none text-sm"
            @input="handleNotesInput"
          />
          <div class="mt-2 flex justify-end">
            <Button 
              size="sm" 
              @click="saveNotes"
              :disabled="isSavingNotes"
              class="text-xs"
            >
              {{ isSavingNotes ? 'Saving...' : 'Save Notes' }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Latest Referral Detail -->
      <div v-if="referrer.latest_referral" class="mt-4 pt-3 border-t border-gray-200">
        <h5 class="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
          <BadgeCheck class="w-4 h-4 text-cosmicPurple" />
          Latest Referral
        </h5>
        <div class="bg-cosmicPurple/5 border border-cosmicPurple/10 rounded-lg p-3 flex items-start gap-3">
          <!-- Applicant avatar -->
          <img
            v-if="referrer.latest_referral.applicant.avatar_image"
            :src="referrer.latest_referral.applicant.avatar_image"
            :alt="`${referrer.latest_referral.applicant.first_name} ${referrer.latest_referral.applicant.last_name}`"
            class="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
          <div v-else class="w-8 h-8 rounded-full bg-cosmicPurple/20 flex items-center justify-center text-cosmicPurple text-xs font-medium flex-shrink-0">
            {{ (referrer.latest_referral.applicant.first_name?.[0] ?? '') + (referrer.latest_referral.applicant.last_name?.[0] ?? '') }}
          </div>
          <!-- Referral info -->
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-800 truncate">
              {{ referrer.latest_referral.applicant.first_name }} {{ referrer.latest_referral.applicant.last_name }}
            </p>
            <p class="text-xs text-gray-500 truncate">
              {{ referrer.latest_referral.role_name }} · {{ referrer.latest_referral.employer_name }}
            </p>
            <div class="flex items-center gap-2 mt-1 flex-wrap">
              <span class="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-cosmicPurple/15 text-cosmicPurple">
                {{ formatReferralStatus(referrer.latest_referral.referral_status) }}
              </span>
              <span v-if="referrer.latest_referral.referral_confirmed_at" class="text-xs text-gray-400">
                {{ formatDate(referrer.latest_referral.referral_confirmed_at) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="mt-4 pt-3 border-t border-gray-200">
        <div class="flex gap-3 justify-end w-full">
          <Button 
            variant="outline" 
            size="sm" 
            class="text-red-600 border-red-200 hover:bg-red-50"
            @click="removeFromCompany"
            :disabled="isUpdating"
          >
            Remove from company
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            class="text-gray-600 border-gray-200 hover:bg-gray-50"
            @click="hideReferrer"
            :disabled="isUpdating"
          >
            Hide referrer
          </Button>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="errorMessage" class="mt-3 pt-3 border-t border-red-200">
      <div class="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { CheckCircle, Circle, ChevronDown, BadgeCheck } from 'lucide-vue-next'
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@/components/ui/hover-card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useReferrersStore } from '@/stores/referrers'
import { useDebounce } from '@/composables/useDebounce'
import { usePrivacy } from '@/composables/usePrivacy'
import { toast } from '@/components/ui/toast/use-toast'

const props = defineProps({
  referrer: {
    type: Object,
    required: true
  },
  companyId: {
    type: [String, Number],
    required: true
  },
  isDefault: {
    type: Boolean,
    default: false
  },
  isExpanded: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['setAsDefault', 'toggleExpansion', 'updateReferrer', 'removeReferrer'])

const referrersStore = useReferrersStore()
const { isBlurred } = usePrivacy()

// Local reactive copy of referrer data for editing
const localReferrer = ref({ ...props.referrer })

// Error state for this specific referrer
const errorMessage = ref('')

// Loading state for button actions
const isUpdating = ref(false)

// Loading state for notes saving
const isSavingNotes = ref(false)

// Watch for prop changes and update local copy
watch(() => props.referrer, (newReferrer) => {
  localReferrer.value = { ...newReferrer }
  // Clear error when referrer data changes
  errorMessage.value = ''
}, { deep: true })

const referrerInitials = computed(() => {
  return props.referrer.name
    .split(' ')
    .map((name) => name[0])
    .join('')
})

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const daysAgo = (dateStr) => {
  if (!dateStr) return null
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000)
}

const latestReferralLabel = (referral) => {
  const days = daysAgo(referral.referral_confirmed_at)
  if (days === null) return 'Last referred'
  if (days === 0) return 'Last referred today'
  return `Last referred ${days} day${days === 1 ? '' : 's'} ago`
}

const formatReferralStatus = (status) => {
  if (status === 'REFERRAL_SUBMITTED_BY_CHAMPION') return 'Submitted by champion'
  if (status === 'REFERRAL_APPROVAL_RECEIVED') return 'Confirmed by applicant'
  return status
}

// Format relationship type for display
const formatRelationshipType = (type) => {
  if (!type) return '—'
  return type
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ')
}

// Format reach difficulty for display
const formatReachDifficulty = (reachDifficulty) => {
  if (!reachDifficulty) return '—'
  return reachDifficulty === 'EASY' ? 'Easy' : 'Hard'
}

// Extract error message from API response
const extractErrorMessage = (error) => {
  if (error.response?.data) {
    const data = error.response.data
    
    // If there's a specific field error, show it
    if (typeof data === 'object' && !Array.isArray(data)) {
      const firstError = Object.entries(data)[0]
      if (firstError) {
        const [field, messages] = firstError
        const message = Array.isArray(messages) ? messages[0] : messages
        return `${field}: ${message}`
      }
    }
    
    // If there's a general error message
    if (typeof data === 'string') {
      return data
    }
    
    // If there's a detail field
    if (data.detail) {
      return data.detail
    }
  }
  
  return 'Failed to update referrer'
}

// Handle setting a referrer as default for a company
const setAsDefault = () => {
  emit('setAsDefault', props.companyId, props.isDefault ? null : props.referrer.id)
}

// Handle status change
const handleStatusChange = async (statusValue) => {
  try {
    errorMessage.value = ''
    const isActive = statusValue === 'active'
    await referrersStore.updateReferrer(props.referrer.id, { is_active: isActive })
    emit('updateReferrer', props.referrer.id, { is_active: isActive })
  } catch (error) {
    console.error('Failed to update status:', error)
    errorMessage.value = extractErrorMessage(error)
  }
}

// Handle relationship type change
const handleRelationshipChange = async (relationshipType) => {
  try {
    errorMessage.value = ''
    const value = relationshipType === undefined ? null : relationshipType
    await referrersStore.updateReferrer(props.referrer.id, { relationship_type: value })
    emit('updateReferrer', props.referrer.id, { relationship_type: value })
  } catch (error) {
    console.error('Failed to update relationship type:', error)
    errorMessage.value = extractErrorMessage(error)
  }
}

// Handle reach difficulty change
const handleReachDifficultyChange = async (value) => {
  try {
    errorMessage.value = ''
    const reachability = value === 'none' || value === undefined ? undefined : value
    await referrersStore.updateReferrer(props.referrer.id, { reachability })
    emit('updateReferrer', props.referrer.id, { reachability })
  } catch (error) {
    console.error('Failed to update reach difficulty:', error)
    errorMessage.value = extractErrorMessage(error)
  }
}

// Toggle referrer expansion
const toggleExpansion = () => {
  emit('toggleExpansion', props.referrer.id)
}

// Save field value with debounce
const saveField = async (fieldName, value) => {
  try {
    errorMessage.value = ''
    const trimmedValue = value ? value.trim() : ''
    const updateData = { [fieldName]: trimmedValue || null }
    await referrersStore.updateReferrer(props.referrer.id, updateData)
    emit('updateReferrer', props.referrer.id, updateData)
  } catch (error) {
    console.error('Failed to update field:', error)
    errorMessage.value = extractErrorMessage(error)
  }
}

// Create debounced version of saveField with 500ms delay
const debouncedSaveField = useDebounce(saveField, 500)

// Handle input changes with debounce
const handleInputChange = (fieldName, value) => {
  // Update local state immediately for responsive UI
  localReferrer.value[fieldName] = value
  
  // Clear any existing error when user starts typing
  errorMessage.value = ''
  
  // Debounce the API call
  debouncedSaveField(fieldName, value)
}

// Handle notes input (no debounce, just update local state)
const handleNotesInput = (event) => {
  localReferrer.value.notes = event.target.value
  // Clear any existing error when user starts typing
  errorMessage.value = ''
}

// Save notes manually
const saveNotes = async () => {
  try {
    isSavingNotes.value = true
    errorMessage.value = ''
    
    const trimmedValue = localReferrer.value.notes ? localReferrer.value.notes.trim() : ''
    const updateData = { notes: trimmedValue || null }
    
    await referrersStore.updateReferrer(props.referrer.id, updateData)
    emit('updateReferrer', props.referrer.id, updateData)
    
    // Show success toast
    toast({
      title: "Notes saved",
      description: "Referrer notes have been successfully updated.",
    })
  } catch (error) {
    console.error('Failed to save notes:', error)
    errorMessage.value = extractErrorMessage(error)
    
    // Show error toast
    toast({
      title: "Save failed",
      description: "Failed to save notes. Please try again.",
      variant: "destructive",
    })
  } finally {
    isSavingNotes.value = false
  }
}

// Remove referrer from the current company
const removeFromCompany = async () => {
  try {
    isUpdating.value = true
    errorMessage.value = ''
    
    // Get current employers and remove the current company
    const currentEmployers = [...(props.referrer.employers || [])]
    const updatedEmployers = currentEmployers.filter(emp => emp !== props.companyId)
    
    await referrersStore.updateReferrer(props.referrer.id, { employers: updatedEmployers })
    
    // Remove from local UI by emitting a removal event
    emit('removeReferrer', props.referrer.id)
  } catch (error) {
    console.error('Failed to remove from company:', error)
    errorMessage.value = extractErrorMessage(error)
  } finally {
    isUpdating.value = false
  }
}

// Hide referrer by setting is_listed to false
const hideReferrer = async () => {
  try {
    isUpdating.value = true
    errorMessage.value = ''
    
    await referrersStore.updateReferrer(props.referrer.id, { is_listed: false })
    
    // Remove from local UI by emitting a removal event instead of update
    emit('removeReferrer', props.referrer.id)
  } catch (error) {
    console.error('Failed to hide referrer:', error)
    errorMessage.value = extractErrorMessage(error)
  } finally {
    isUpdating.value = false
  }
}
</script>
